import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import type { CSSProperties, JSX, ReactNode } from 'react';
import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import type { TAnimationDirection, TSecondaryNav } from '@feathr/extender/state';
import {
  getSecondaryNavFromPath,
  NavigationProvider,
  useFlags,
  useNavigation,
  useUser,
} from '@feathr/extender/state';
import { useOverflowDetection } from '@feathr/hooks';

import AppLayoutV1 from './AppLayoutV1';
import CommunitySecondaryNav from './Page/CommunitySecondaryNav';
import HomeSecondaryNav from './Page/HomeSecondaryNav';
import PrimaryNav from './Page/PrimaryNav';
import NavBottomSection from './Page/PrimaryNav/NavBottomSection';
import { resolveNavPreference } from './Page/PrimaryNav/navPreference';
import ProjectsSecondaryNav from './Page/ProjectsSecondaryNav';
import ReportsSecondaryNav from './Page/ReportsSecondaryNav';
import SettingsSecondaryNav from './Page/SettingsSecondaryNav';

import * as styles from './AppLayout.css';

interface IAppLayoutProps {
  children: ReactNode;
}

function getSlideVariants(shouldReduceMotion: boolean | null) {
  const getOffset = (direction: TAnimationDirection, invert = false): number => {
    if (shouldReduceMotion) {
      return 0;
    }
    const offset = 75;
    const isRight = direction === 'right';
    if (invert) {
      return isRight ? -offset : offset;
    }
    return isRight ? offset : -offset;
  };

  return {
    enter: (direction: TAnimationDirection) => ({
      x: getOffset(direction),
      opacity: shouldReduceMotion ? 1 : 0,
    }),
    center: { x: 0, opacity: 1 },
    exit: (direction: TAnimationDirection) => ({
      x: getOffset(direction, true),
      opacity: shouldReduceMotion ? 1 : 0,
    }),
  };
}

function getAnimationKey(activeSecondaryNav: TSecondaryNav, isMinimized: boolean): string {
  if (activeSecondaryNav === null) {
    return 'primary';
  }
  if (isMinimized) {
    return 'minimized';
  }
  return activeSecondaryNav;
}

function NavigationUrlSync(): null {
  const location = useLocation();
  const { isMinimized, activeSecondaryNav, setActiveSecondaryNav } = useNavigation();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    if (prevPathRef.current === location.pathname) {
      return;
    }
    prevPathRef.current = location.pathname;

    if (!isMinimized && activeSecondaryNav !== null) {
      const expectedSecondaryNav = getSecondaryNavFromPath(location.pathname);
      if (expectedSecondaryNav !== activeSecondaryNav) {
        setActiveSecondaryNav(expectedSecondaryNav);
      }
    }
  }, [location.pathname, isMinimized, activeSecondaryNav, setActiveSecondaryNav]);

  return null;
}

function AppLayoutContent({ children }: Readonly<IAppLayoutProps>): JSX.Element {
  const {
    navWidth,
    isMinimized,
    activeSecondaryNav,
    animationDirection,
    setIsAnimating,
    scrollableRef,
  } = useNavigation();
  const shouldReduceMotion = useReducedMotion();
  const { hasOverflow } = useOverflowDetection(scrollableRef);

  const style: CSSProperties = {
    '--l-nav-width': `${navWidth}px`,
  } as CSSProperties;

  const slideVariants = getSlideVariants(shouldReduceMotion);

  const renderNav = (): JSX.Element => {
    if (isMinimized) {
      return <PrimaryNav />;
    }

    switch (activeSecondaryNav) {
      case 'home':
        return <HomeSecondaryNav />;

      case 'projects':
        return <ProjectsSecondaryNav />;

      case 'community':
        return <CommunitySecondaryNav />;

      case 'reports':
        return <ReportsSecondaryNav />;

      case 'settings':
        return <SettingsSecondaryNav />;

      default:
        return <PrimaryNav />;
    }
  };

  return (
    <div className={styles.root} style={style}>
      <NavigationUrlSync />
      {children}
      <div className={styles.sidebarWrapper}>
        <AnimatePresence
          custom={animationDirection}
          mode={'popLayout'}
          onExitComplete={(): void => setIsAnimating(false)}
        >
          <motion.div
            animate={'center'}
            className={styles.sidebar}
            custom={animationDirection}
            exit={'exit'}
            initial={'enter'}
            key={getAnimationKey(activeSecondaryNav, isMinimized)}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            variants={slideVariants}
          >
            {renderNav()}
          </motion.div>
        </AnimatePresence>
        <NavBottomSection className={styles.navBottom} showShadow={hasOverflow} />
      </div>
    </div>
  );
}

function AppLayout({ children }: Readonly<IAppLayoutProps>): JSX.Element {
  const { showNewPrimaryNav } = useFlags();
  const user = useUser();

  const userNavPreference = user?.getSetting('use_new_primary_nav');
  const useNewNav = resolveNavPreference(userNavPreference, showNewPrimaryNav);

  if (!useNewNav) {
    return <AppLayoutV1>{children}</AppLayoutV1>;
  }

  return (
    <NavigationProvider>
      <AppLayoutContent>{children}</AppLayoutContent>
    </NavigationProvider>
  );
}

export default observer(AppLayout);
